
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

interface MasterNavigationProps {
  currentPath?: string;
}

export default function MasterNavigation({ currentPath = '/' }: MasterNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Lock body scroll while the menu is open so the blurred backdrop stays put.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const navigationItems = [
    { path: '/', label: 'Home', icon: 'ri-home-line' },
    { path: '/services', label: 'Services', icon: 'ri-shield-check-line' },
    { path: '/ai-agents', label: 'AI Agents', icon: 'ri-robot-2-line' },
    { path: '/methodology', label: 'Methodology', icon: 'ri-flow-chart' },
    { path: '/case-studies', label: 'Case Studies', icon: 'ri-file-list-3-line' },
    { path: '/faq', label: 'FAQ', icon: 'ri-question-line' },
    { path: '/disclosure', label: 'Responsible Disclosure', icon: 'ri-bug-line' },
    { path: '/blog', label: 'Blog', icon: 'ri-article-line' },
    { path: '/client-registration', label: 'Register', icon: 'ri-user-add-line' },
    { path: '/contact', label: 'Contact', icon: 'ri-customer-service-line' }
  ];

  return (
    <div className="relative">
      {/* Master Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-[#6B2737] hover:bg-[#8B3747] text-white px-4 py-2 rounded-lg transition-all hover:scale-105 whitespace-nowrap z-50"
      >
        <i className={`${isOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
        <span className="hidden sm:inline">Menu</span>
      </button>

      {/* Master Navigation Overlay — rendered via a portal to document.body so it
          escapes the nav's backdrop-filter (which otherwise becomes the containing
          block for fixed children once the page is scrolled, breaking the blur). */}
      {isOpen && createPortal(
        <>
          {/* Backdrop with stronger blur and fixed positioning */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-lg z-[999998]"
            onClick={() => setIsOpen(false)}
          ></div>

          {/* Navigation Panel */}
          <div className="fixed top-0 right-0 h-full w-96 bg-black border-l border-gray-800 z-[999999] transform transition-transform duration-300 ease-out">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-8 w-8 object-contain" />
                  <span className="text-white font-light text-lg">TIDHQ.NETWORK</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Navigation Items */}
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all hover:bg-[#1A3A2E] group ${
                      currentPath === item.path ? 'bg-[#6B2737] text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <i className={`${item.icon} text-lg ${currentPath === item.path ? 'text-white' : 'text-[#6B2737] group-hover:text-white'}`}></i>
                    <span className="font-medium">{item.label}</span>
                    {currentPath === item.path && (
                      <i className="ri-arrow-right-line ml-auto text-white"></i>
                    )}
                  </Link>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <div className="text-gray-400 text-xs uppercase tracking-wider mb-4">Quick Actions</div>
                <div className="space-y-2">
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 bg-[#6B2737] hover:bg-[#8B3747] text-white rounded-lg transition-all hover:scale-105"
                  >
                    <i className="ri-calendar-line"></i>
                    <span>Book Consultation</span>
                  </Link>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <div className="text-gray-400 text-xs uppercase tracking-wider mb-4">Contact</div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-400 text-sm">
                    <i className="ri-mail-line text-[#6B2737]"></i>
                    <span>contact@tidhq.net</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#6B2737] transition-colors">
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#6B2737] transition-colors">
                      <i className="ri-twitter-x-line text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}